/** Recursive editor for one JSON-schema property node.
 *
 * Renders inside an Accordion: key (rename), title, description, type, format,
 * required (top-level only), group (top-level only), display order, the `x-`
 * content flags (multilingual / template / html), an options (enum) editor for
 * scalars, and — for object/array types — a nested child editor (this same
 * component, recursively).
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import {
  defaultForType,
  renameChildProperty,
  setPropField,
  setPropFlag,
  setPropType,
} from './schemaEditorOps';
import {
  SCHEMA_TYPES,
  X_DISPLAY_ORDER,
  X_ENUM_LABELS,
  X_GROUP,
  X_HTML,
  X_MULTILINGUAL,
  X_TEMPLATE,
  type JsonSchemaType,
  type SchemaGroup,
  type SchemaProp,
} from './vocabulary';

interface Props {
  propKey: string;
  prop: SchemaProp;
  readOnly: boolean;
  /** When present, show the group selector (top-level properties only). */
  groups?: SchemaGroup[];
  /** When present, show the required toggle (top-level properties only). */
  required?: boolean;
  onRename?: (newKey: string) => void;
  onChange: (next: SchemaProp) => void;
  onRemove?: () => void;
  onToggleRequired?: (v: boolean) => void;
  defaultExpanded?: boolean;
  /** Array-item editor: hide the key row + remove button. */
  isItems?: boolean;
}

const SCALAR_TYPES: JsonSchemaType[] = ['string', 'number', 'integer'];

/** Height of a collapsed property row. Dense, so a long schema fits more rows per screen
 *  (CP-7988); the editor's drag handles center on it. */
export const PROPERTY_ROW_HEIGHT = 36;

// Common JSON-Schema string formats — suggested in the format combobox, but any
// value is allowed (freeSolo). The data editor specially handles date/time/date-time.
const COMMON_FORMATS = [
  'date-time', 'date', 'time', 'duration', 'email', 'uri', 'uri-reference',
  'uuid', 'hostname', 'ipv4', 'ipv6', 'regex', 'json-pointer',
];

const DENSE_CHIP = { height: 20, fontSize: '0.7rem' } as const;

function linesToEnum(text: string, type: JsonSchemaType | undefined): unknown[] {
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (type === 'number' || type === 'integer') {
    return lines.map((l) => Number(l)).filter((n) => !Number.isNaN(n));
  }
  return lines;
}
function enumToLines(values: unknown[] | undefined): string {
  return (values ?? []).map((v) => String(v)).join('\n');
}

export function SchemaPropertyEditor(props: Props) {
  const { propKey, prop, required, defaultExpanded, isItems } = props;
  const type = prop.type ?? 'string';

  const flagChips = (
    <>
      {prop[X_MULTILINGUAL] === true && <Chip size="small" color="info" label="i18n" sx={DENSE_CHIP} />}
      {prop[X_TEMPLATE] === true && <Chip size="small" variant="outlined" label="template" sx={DENSE_CHIP} />}
      {prop[X_HTML] === true && <Chip size="small" variant="outlined" label="HTML" sx={DENSE_CHIP} />}
      {required && <Chip size="small" color="warning" label="required" sx={DENSE_CHIP} />}
    </>
  );

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ minHeight: PROPERTY_ROW_HEIGHT, '& .MuiAccordionSummary-content': { my: 0.5 } }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {isItems ? 'Item schema' : propKey || '(unnamed)'}
          </Typography>
          <Chip size="small" variant="outlined" label={type} sx={DENSE_CHIP} />
          {flagChips}
          <Box sx={{ flex: 1 }} />
          {prop.title ? (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
              {prop.title}
            </Typography>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <SchemaPropertyFields {...props} />
      </AccordionDetails>
    </Accordion>
  );
}

/** A property's editable fields: the body of `SchemaPropertyEditor`, and what a
 *  row of the two-column schema editor opens inline (CP-7988). */
export function SchemaPropertyFields({
  propKey,
  prop,
  readOnly,
  groups,
  required,
  onRename,
  onChange,
  onRemove,
  onToggleRequired,
  isItems,
}: Props) {
  const [keyDraft, setKeyDraft] = useState(propKey);
  const type = prop.type ?? 'string';
  const isScalar = SCALAR_TYPES.includes(type);
  const set = <K extends keyof SchemaProp>(field: K, value: SchemaProp[K] | undefined) =>
    onChange(setPropField(prop, field, value));

  // --- nested children (object) ---
  const childProps = prop.properties ?? {};
  const childEntries = Object.entries(childProps);
  const [newChildKey, setNewChildKey] = useState('');
  const addChild = () => {
    const k = newChildKey.trim();
    if (!k || k in childProps) return;
    onChange({ ...prop, properties: { ...childProps, [k]: defaultForType('string') } });
    setNewChildKey('');
  };
  const setChild = (k: string, cp: SchemaProp) =>
    onChange({ ...prop, properties: { ...childProps, [k]: cp } });
  const removeChild = (k: string) => {
    const next = { ...childProps };
    delete next[k];
    onChange({ ...prop, properties: next });
  };
  const renameChild = (oldK: string, newK: string) =>
    onChange(renameChildProperty(prop, oldK, newK));

  return (
    <Stack spacing={1.5}>
      {!isItems && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            label="Key"
            size="small"
            value={keyDraft}
            disabled={readOnly || !onRename}
            onChange={(e) => setKeyDraft(e.target.value)}
            onBlur={() => {
              const nk = keyDraft.trim();
              if (nk && nk !== propKey) onRename?.(nk);
              else setKeyDraft(propKey);
            }}
            sx={{ width: 220 }}
          />
          <Box sx={{ flex: 1 }} />
          {onRemove && (
            <IconButton size="small" color="error" disabled={readOnly} onClick={onRemove} aria-label="Remove property">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select label="Type" size="small" value={type} disabled={readOnly}
          onChange={(e) => onChange(setPropType(prop, e.target.value as JsonSchemaType))}
          sx={{ width: 150 }}
        >
          {SCHEMA_TYPES.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Title" size="small" value={prop.title ?? ''} disabled={readOnly}
          onChange={(e) => set('title', e.target.value)} sx={{ minWidth: 200, flex: 1 }}
        />
        {isScalar && (
          <Autocomplete
            freeSolo size="small" options={COMMON_FORMATS} disabled={readOnly}
            inputValue={prop.format ?? ''}
            onInputChange={(_, v) => set('format', v || undefined)}
            sx={{ width: 200 }}
            renderInput={(params) => (
              <TextField {...params} label="Format" placeholder="date-time, email…" />
            )}
          />
        )}
      </Stack>

      <TextField
        label="Description" size="small" fullWidth multiline minRows={1} maxRows={4}
        value={prop.description ?? ''} disabled={readOnly}
        onChange={(e) => set('description', e.target.value)}
      />

      {/* Placement + ordering (top-level only shows group). */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        {groups && (
          <TextField
            select label="Group" size="small" value={prop[X_GROUP] ?? ''} disabled={readOnly}
            onChange={(e) => set(X_GROUP, e.target.value || undefined)} sx={{ width: 180 }}
          >
            <MenuItem value="">(Ungrouped)</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g.name} value={g.name}>{g.title || g.name}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Display order" size="small" type="number" disabled={readOnly}
          value={prop[X_DISPLAY_ORDER] ?? ''} sx={{ width: 140 }}
          onChange={(e) => set(X_DISPLAY_ORDER, e.target.value === '' ? undefined : Number(e.target.value))}
        />
        {required !== undefined && (
          <FormControlLabel
            control={
              <Switch checked={required} disabled={readOnly} size="small"
                onChange={(e) => onToggleRequired?.(e.target.checked)} />
            }
            label="Required"
          />
        )}
      </Stack>

      {/* Content flags. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <FormControlLabel
          control={<Switch size="small" checked={prop[X_MULTILINGUAL] === true} disabled={readOnly}
            onChange={(e) => onChange(setPropFlag(prop, X_MULTILINGUAL, e.target.checked))} />}
          label="Multilingual"
        />
        <FormControlLabel
          control={<Switch size="small" checked={prop[X_TEMPLATE] === true} disabled={readOnly}
            onChange={(e) => onChange(setPropFlag(prop, X_TEMPLATE, e.target.checked))} />}
          label="Django template"
        />
        <FormControlLabel
          control={<Switch size="small" checked={prop[X_HTML] === true} disabled={readOnly}
            onChange={(e) => onChange(setPropFlag(prop, X_HTML, e.target.checked))} />}
          label="HTML content"
        />
        {type === 'string' && (
          <FormControlLabel
            control={<Switch size="small" checked={prop.multiline === true} disabled={readOnly}
              onChange={(e) => onChange(setPropFlag(prop, 'multiline', e.target.checked))} />}
            label="Multiline"
          />
        )}
      </Stack>

      {/* Options (enum) for scalars. */}
      {isScalar && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            label="Options (one per line)" size="small" multiline minRows={1} maxRows={6}
            value={enumToLines(prop.enum)} disabled={readOnly} sx={{ minWidth: 220, flex: 1 }}
            onChange={(e) => {
              const values = linesToEnum(e.target.value, type);
              set('enum', values.length ? values : undefined);
            }}
          />
          <TextField
            label="Option labels (optional)" size="small" multiline minRows={1} maxRows={6}
            value={(prop[X_ENUM_LABELS] ?? []).join('\n')} disabled={readOnly} sx={{ minWidth: 220, flex: 1 }}
            onChange={(e) => {
              const labels = e.target.value.split('\n').map((l) => l.trim());
              const trimmed = labels.filter((l) => l.length > 0);
              set(X_ENUM_LABELS, trimmed.length ? labels : undefined);
            }}
          />
        </Stack>
      )}

      {/* Nested object children. */}
      {type === 'object' && (
        <Box>
          <Divider textAlign="left" sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">Fields</Typography>
          </Divider>
          <Stack spacing={1}>
            {childEntries.map(([ck, cp]) => (
              <SchemaPropertyEditor
                key={ck}
                propKey={ck}
                prop={cp}
                readOnly={readOnly}
                onRename={(nk) => renameChild(ck, nk)}
                onChange={(next) => setChild(ck, next)}
                onRemove={() => removeChild(ck)}
              />
            ))}
            {!readOnly && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small" placeholder="new field key" value={newChildKey}
                  onChange={(e) => setNewChildKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChild(); } }}
                  sx={{ width: 220 }}
                />
                <Button size="small" startIcon={<AddIcon />} onClick={addChild} disabled={!newChildKey.trim()}>
                  Add field
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      )}

      {/* Array item schema. */}
      {type === 'array' && (
        <Box>
          <Divider textAlign="left" sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">Array items</Typography>
          </Divider>
          <SchemaPropertyEditor
            propKey=""
            isItems
            prop={prop.items ?? { type: 'string' }}
            readOnly={readOnly}
            onChange={(next) => onChange({ ...prop, items: next })}
          />
        </Box>
      )}
    </Stack>
  );
}
